import {
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { useTranslation } from 'react-i18next'
import { HexColorPicker } from 'react-colorful'
import Icon from 'components/priority/Icon/Icon'
import {
  EDITOR_TEXT_SHADOW_SEED,
  cssColorToHex,
  editorTextBoxStyleActions,
  formatPt,
  type EditorStylingAction,
  type EditorTextBoxSwatch,
  type EditorTextShadow,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import ColorTileGrid from './ColorTileGrid'
import * as S from './EditorTextBoxToolbar.styled'
import * as M from './EditorTextBoxShadowMenu.styled'

type ShadowAxis = 'x' | 'y' | 'blur' | 'opacity'

// Offsets and blur step by half a point, opacity by ten percent; the ranges
// keep a shadow near its text, well inside what the box's row accepts.
const STEP: Record<ShadowAxis, number> = {
  x: 0.5,
  y: 0.5,
  blur: 0.5,
  opacity: 0.1,
}
const RANGE: Record<ShadowAxis, readonly [number, number]> = {
  x: [-36, 36],
  y: [-36, 36],
  blur: [0, 36],
  opacity: [0, 1],
}

const clampAxis = (axis: ShadowAxis, value: number): number => {
  const [min, max] = RANGE[axis]
  return Math.min(max, Math.max(min, Math.round(value * 100) / 100))
}

/** What a row shows: points as the toolbar prints them, opacity in percent. */
const shownValue = (axis: ShadowAxis, shadow: EditorTextShadow): string =>
  axis === 'opacity'
    ? String(Math.round(shadow.opacity * 100))
    : formatPt(shadow[axis])

/** A typed number in the shadow's own units, or null when it is not one. */
const typedValue = (axis: ShadowAxis, typed: string): number | null => {
  const trimmed = typed.trim()
  const n = Number(trimmed)
  if (trimmed === '' || !Number.isFinite(n)) return null
  return clampAxis(axis, axis === 'opacity' ? n / 100 : n)
}

type EditorTextBoxShadowMenuProps = {
  /** The box's shadow as the mount holds it now; null when it has none. */
  readShadow: () => EditorTextShadow | null
  /** Styles the held selection and keeps the popover open. */
  apply: (action: EditorStylingAction) => void
  swatches: readonly EditorTextBoxSwatch[]
}

/**
 * The text shadow's controls: offsets, blur and opacity steppers, a colour
 * row, and Remove. A box without a shadow shows the seed and writes nothing
 * until a control changes; every change is one undoable edit of the whole
 * shadow, taken from what the box holds at that moment.
 */
export const EditorTextBoxShadowMenu = ({
  readShadow,
  apply,
  swatches,
}: EditorTextBoxShadowMenuProps) => {
  const { t } = useTranslation()
  const [shadow, setShadow] = useState(readShadow)
  const [drafts, setDrafts] = useState<Partial<Record<ShadowAxis, string>>>({})
  const [view, setView] = useState<'tiles' | 'hex'>('tiles')
  const shown = shadow ?? EDITOR_TEXT_SHADOW_SEED
  const [hex, setHex] = useState(shown.color)
  const hexViewRef = useRef<HTMLDivElement>(null)

  // The hex view is taller than the tiles it replaces: where the window caps
  // the popover, opening it scrolls the popover to show the whole view, the
  // picker down to Apply.
  useLayoutEffect(() => {
    if (view === 'hex') hexViewRef.current?.scrollIntoView({ block: 'nearest' })
  }, [view])

  const write = (next: EditorTextShadow | null) => {
    apply(editorTextBoxStyleActions.textShadow(next))
    setShadow(readShadow())
  }
  const change = (patch: Partial<EditorTextShadow>) =>
    write({ ...(readShadow() ?? EDITOR_TEXT_SHADOW_SEED), ...patch })
  const remove = () => {
    if (readShadow() !== null) write(null)
  }
  const step =
    (axis: ShadowAxis, sign: 1 | -1) => (e: ReactMouseEvent<HTMLElement>) => {
      e.preventDefault()
      const base = readShadow() ?? EDITOR_TEXT_SHADOW_SEED
      change({ [axis]: clampAxis(axis, base[axis] + sign * STEP[axis]) })
    }
  const dropDraft = (axis: ShadowAxis) =>
    setDrafts((d) => ({ ...d, [axis]: undefined }))
  const commitDraft = (axis: ShadowAxis) => {
    const typed = drafts[axis]
    dropDraft(axis)
    const value = typed === undefined ? null : typedValue(axis, typed)
    if (value !== null) change({ [axis]: value })
  }
  const pickColor = (css: string) => {
    const picked = cssColorToHex(css)
    if (picked !== null) change({ color: picked })
  }

  const rows: {
    axis: ShadowAxis
    label: string
    less: string
    more: string
  }[] = [
    {
      axis: 'x',
      label: t('editor.textBox.toolbar.shadowOffsetX'),
      less: t('editor.textBox.toolbar.shadowLeft'),
      more: t('editor.textBox.toolbar.shadowRight'),
    },
    {
      axis: 'y',
      label: t('editor.textBox.toolbar.shadowOffsetY'),
      less: t('editor.textBox.toolbar.shadowUp'),
      more: t('editor.textBox.toolbar.shadowDown'),
    },
    {
      axis: 'blur',
      label: t('editor.textBox.toolbar.shadowBlur'),
      less: t('editor.textBox.toolbar.shadowSharper'),
      more: t('editor.textBox.toolbar.shadowSofter'),
    },
    {
      axis: 'opacity',
      label: t('editor.textBox.toolbar.shadowOpacity'),
      less: t('editor.textBox.toolbar.shadowLighter'),
      more: t('editor.textBox.toolbar.shadowDarker'),
    },
  ]

  return (
    <M.ShadowMenu data-editor-text-shadow-menu $hex={view === 'hex'}>
      {rows.map((row) => (
        <div key={row.axis}>
          <M.ShadowLabel>{row.label}</M.ShadowLabel>
          <S.StepperRow {...{ [`data-editor-text-shadow-${row.axis}`]: true }}>
            <S.Btn
              type="button"
              data-editor-text-shadow-dec
              title={row.less}
              onMouseDown={step(row.axis, -1)}
            >
              <Icon icon="minusIcon" width="14px" height="14px" />
            </S.Btn>
            <S.SizeInput
              data-editor-text-shadow-input
              title={row.label}
              inputMode="decimal"
              value={drafts[row.axis] ?? shownValue(row.axis, shown)}
              onFocus={(e) => {
                const { value } = e.currentTarget
                setDrafts((d) => ({ ...d, [row.axis]: value }))
                e.currentTarget.select()
              }}
              onChange={(e) => {
                const value = e.currentTarget.value.replace(/[^0-9.-]/g, '')
                setDrafts((d) => ({ ...d, [row.axis]: value }))
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  e.stopPropagation()
                  commitDraft(row.axis)
                } else if (e.key === 'Escape') {
                  dropDraft(row.axis)
                }
              }}
              onBlur={() => dropDraft(row.axis)}
            />
            <S.Btn
              type="button"
              data-editor-text-shadow-inc
              title={row.more}
              onMouseDown={step(row.axis, 1)}
            >
              <Icon icon="plus" width="14px" height="14px" />
            </S.Btn>
          </S.StepperRow>
        </div>
      ))}
      <M.ShadowLabel>{t('editor.textBox.toolbar.shadowColor')}</M.ShadowLabel>
      <ColorTileGrid
        hidden={view !== 'tiles'}
        attrPrefix="shadow"
        swatches={swatches}
        onPick={pickColor}
        onRemove={remove}
        onCustom={() => {
          setHex(shown.color)
          setView('hex')
        }}
      />
      <S.HexView ref={hexViewRef} hidden={view !== 'hex'}>
        <S.TextBtn
          type="button"
          data-editor-text-shadow-tiles-back
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setView('tiles')}
        >
          {t('editor.textBox.toolbar.backToTiles')}
        </S.TextBtn>
        <HexColorPicker color={hex} onChange={setHex} />
        <S.Row>
          <S.TextInput
            data-editor-text-shadow-input-hex
            value={hex}
            onChange={(e) => setHex(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') pickColor(hex)
            }}
          />
          <S.ApplyBtn
            type="button"
            data-editor-text-shadow-apply
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => pickColor(hex)}
          >
            {t('editor.textBox.toolbar.apply')}
          </S.ApplyBtn>
        </S.Row>
      </S.HexView>
      <S.MenuRule />
      <S.MenuItem
        type="button"
        data-editor-text-shadow-clear
        disabled={shadow === null}
        onMouseDown={(e) => e.preventDefault()}
        onClick={remove}
      >
        {t('editor.textBox.toolbar.removeShadow')}
      </S.MenuItem>
    </M.ShadowMenu>
  )
}
